import React, { useState } from "react";
import { motion } from "framer-motion";
import { Star, X, Award } from "lucide-react";
import JobApplicationModal from "../components/JobApplicationModal";
import CaptchaModal from "../components/CaptchaModal";

const CareersPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [vacanciesOpen, setVacanciesOpen] = useState(false);

  // Fetch vacancy status from settings
  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_SETTINGS_URL || "https://vintage-data.vercel.app/settings.json");
        if (response.ok) {
          const settings = await response.json();
          setVacanciesOpen(settings.isVacancyOpen);
        }
      } catch (error) {
        console.error("Error fetching vacancy status:", error);
      }
    };
    fetchSettings();
  }, []);

  // Boolean function to determine vacancy status
  const isVacancyOpen = () => {
    return vacanciesOpen;
  };

  const jobDescription = `
Join our passionate team at Vintage Dealership! We're seeking dedicated individuals 
who share our love for classic automobiles and automotive heritage. Be part of a 
distinguished environment where you'll work with prestigious vintage vehicles and 
discerning collectors. We value craftsmanship, attention to detail, and a deep 
appreciation for automotive history.
`;

  return (
    <div
      className="min-vh-100 position-relative"
      style={{
        background: "var(--bg-dark)",
        paddingTop: "100px",
      }}
    >
      {/* Background Image with Overlay */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: "url(https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?q=80&w=2304&auto=format&fit=crop)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.15,
          zIndex: 0,
        }}
      />

      <div className="container py-5 position-relative" style={{ zIndex: 2 }}>
        <motion.div
          className="mt-5"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h3
            className="fw-bold text-center mb-5"
            style={{
              fontSize: "clamp(2rem, 4vw, 3rem)",
              color: "var(--text-main)",
              fontFamily: "var(--font-heading)",
            }}
          >
            Careers @ Vintage Dealership
          </h3>

          <div className="row g-5 d-flex align-items-stretch justify-content-center">
            {/* -------- SECTION: VACANCIES OPEN -------- */}
            {isVacancyOpen() ? (
              <div className="col-12 col-lg-8 d-flex">
                <motion.div
                  className="p-5 rounded-4 h-100 w-100 glass-panel"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    border: "1px solid var(--glass-border)",
                    boxShadow: "var(--glass-shadow)",
                  }}
                >
                  <h2
                    className="fw-bold mb-4 d-flex align-items-center gap-3"
                    style={{ color: "var(--text-main)", fontFamily: "var(--font-heading)" }}
                  >
                    <Star size={32} className="text-accent" />
                    Vacancies Open
                  </h2>

                  <p
                    style={{
                      whiteSpace: "pre-line",
                      lineHeight: "1.8",
                      color: "var(--text-muted)",
                      fontSize: "1.1rem",
                    }}
                  >
                    {jobDescription}
                  </p>

                  <div className="text-center mt-5">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowCaptcha(true)}
                      className="btn btn-luxury"
                      style={{
                        borderRadius: "4px",
                      }}
                    >
                      <Award size={22} className="me-2" />
                      Apply Now
                    </motion.button>
                  </div>
                </motion.div>
              </div>
            ) : (
              /* -------- SECTION: VACANCIES CLOSED -------- */
              <div className="col-12 col-lg-8 d-flex">
                <motion.div
                  className="p-5 rounded-4 h-100 w-100 glass-panel"
                  style={{
                    border: "1px solid var(--glass-border)",
                  }}
                >
                  <h2
                    className="fw-bold mb-4 d-flex align-items-center gap-3"
                    style={{ color: "var(--text-main)", fontFamily: "var(--font-heading)" }}
                  >
                    <X size={32} style={{ color: "var(--text-muted)" }} />
                    Vacancies Closed
                  </h2>

                  <p style={{ color: "var(--text-muted)", fontSize: "1.1rem" }}>
                    These positions are currently filled. Follow our updates for
                    future opportunities at Vintage Dealership.
                  </p>

                  <ul className="mt-4 list-unstyled" style={{ color: "var(--text-muted)" }}>
                    <li className="mb-2">Sales Trainee</li>

                  </ul>
                </motion.div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <JobApplicationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setCaptchaToken(null);
        }}
        captchaToken={captchaToken}
      />
      <CaptchaModal
        show={showCaptcha}
        onHide={() => setShowCaptcha(false)}
        onVerified={(token) => {
          setCaptchaToken(token);
          setShowCaptcha(false);
          setIsModalOpen(true);
        }}
      />
    </div>
  );
};

export default CareersPage;
